/**
 * Channel Item Component
 * Individual channel in the sidebar list
 * Supports drag-and-drop reordering when `draggable` prop is true
 */

import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Channel } from '@/types/api.types';
import { useAppStore } from '@/store/app.store';
import { VoiceChannelIndicator } from '@/components/voice';
import { useChannelNotificationLevel } from '@/hooks/useChannelNotificationLevel';
import { useChannelMentionCount } from '@/hooks/useMentions';
import { BellOff, AtSign, Settings, GripVertical } from 'lucide-react';
import { ChannelSettingsModal } from './ChannelSettingsModal';

interface ChannelItemProps {
  channel: Channel;
  draggable?: boolean;
}

const ChannelItem = ({ channel, draggable = false }: ChannelItemProps) => {
  const { activeChannelId, setActiveChannel } = useAppStore();
  const isActive = activeChannelId === channel.id;
  const [showSettings, setShowSettings] = useState(false);

  // Get notification level for this channel
  const { data: notificationLevel } = useChannelNotificationLevel(channel.id);
  const isMuted = notificationLevel === 'none';

  // Get unread mention count
  const { data: mentionCount } = useChannelMentionCount(channel.id);

  // DnD sortable hook — only active when draggable
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: channel.id, disabled: !draggable });

  const style = draggable
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
      }
    : undefined;

  const handleClick = () => {
    setActiveChannel(channel.id);
  };

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent channel selection
    setShowSettings(true);
  };

  // Channel type icons
  const getChannelIcon = () => {
    switch (channel.type) {
      case 'private':
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        );
      case 'announcement':
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
            />
          </svg>
        );
      default:
        return <span className="text-sm font-medium">#</span>;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={`relative w-full flex items-center px-2 py-1.5 rounded-md text-left transition-colors group cursor-pointer ${
        isActive
          ? 'bg-primary-500/15 text-primary-400'
          : 'text-gray-300 hover:bg-dark-bg-hover/50 hover:text-white'
      } ${isDragging ? 'opacity-50 shadow-lg z-10' : ''}`}
    >
      {/* Drag Handle — only for admin/super_admin */}
      {draggable && (
        <span
          {...listeners}
          className="flex-shrink-0 mr-1 cursor-grab active:cursor-grabbing p-0.5 text-gray-500 hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
          title="Плъзни за преподреждане"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </span>
      )}

      {/* Active accent bar */}
      {isActive && (
        <div className="absolute left-0 top-1/4 bottom-1/4 w-[3px] rounded-full bg-primary-500" />
      )}

      {/* Channel Icon */}
      <span
        className={`flex-shrink-0 mr-2 ${
          isActive ? 'text-primary-400' : 'text-gray-400 group-hover:text-gray-300'
        }`}
      >
        {getChannelIcon()}
      </span>

      {/* Channel Name */}
      <span className="flex-1 min-w-0 text-sm font-medium truncate">
        {channel.name}
      </span>

      {/* Settings Button (visible on hover) */}
      <button
        onClick={handleSettingsClick}
        className={`flex-shrink-0 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity ${
          isActive
            ? 'text-primary-300/70 hover:text-primary-200 hover:bg-primary-500/10'
            : 'text-gray-400 hover:text-gray-200 hover:bg-dark-bg-hover'
        }`}
        title="Настройки на канала"
      >
        <Settings className="w-3.5 h-3.5" />
      </button>

      {/* Mute Indicator */}
      {isMuted && (
        <span title="Заглушен канал">
          <BellOff
            className={`flex-shrink-0 w-3.5 h-3.5 ml-1 ${
              isActive ? 'text-white/70' : 'text-gray-500'
            }`}
            aria-label="Заглушен канал"
          />
        </span>
      )}

      {/* Voice Indicator */}
      <VoiceChannelIndicator channelId={channel.id} />

      {/* Mention Badge (higher priority than unread) */}
      {mentionCount > 0 && (
        <span
          className="flex-shrink-0 ml-2 px-1.5 py-0.5 text-xs font-bold bg-amber-500 text-white rounded-full flex items-center gap-0.5 shadow-sm"
          title={`${mentionCount} непрочетени споменавания`}
        >
          <AtSign className="w-3 h-3" />
          {mentionCount > 9 ? '9+' : mentionCount}
        </span>
      )}

      {/* Unread Badge (only show if no mentions) */}
      {!mentionCount && (channel.unread_count ?? 0) > 0 && (
        <span className="flex-shrink-0 ml-2 px-2 py-0.5 text-xs font-bold bg-accent-500 text-white rounded-full">
          {(channel.unread_count ?? 0) > 99 ? '99+' : channel.unread_count}
        </span>
      )}

      {/* Archived Indicator */}
      {channel.is_archived && (
        <span className="flex-shrink-0 ml-2 text-xs text-gray-500">
          (архивиран)
        </span>
      )}

      {/* Channel Settings Modal */}
      {showSettings && (
        <ChannelSettingsModal
          channel={channel}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
};

export default ChannelItem;
